import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("A senha deve ter ao menos 6 caracteres.");
    if (password !== confirm) return toast.error("As senhas não coincidem.");
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Falha ao redefinir.");
      toast.success("Senha redefinida com sucesso! Faça login.");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error("Não foi possível redefinir", { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-blue-950 px-5 py-10">
      <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
        <div className="rounded-3xl border border-white/10 bg-white p-8 shadow-2xl">
          <div className="flex flex-col items-center text-center">
            <img src="/veloclin-logo.png" alt="VeloClin" className="h-16 w-auto" />
            <h1 className="mt-4 font-heading text-2xl font-extrabold tracking-tight text-slate-900">Nova senha</h1>
            <p className="mt-1 text-sm text-slate-500">Defina uma nova senha para sua conta.</p>
          </div>

          {!token ? (
            <p data-testid="reset-no-token" className="mt-7 rounded-2xl border border-amber-100 bg-amber-50 p-5 text-center text-sm text-amber-700">
              Link inválido. Solicite a redefinição novamente na tela de login.
            </p>
          ) : (
            <form onSubmit={submit} className="mt-7 space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-600">Nova senha</label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input data-testid="reset-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="border-slate-200 pl-9 focus-visible:ring-blue-600" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Confirmar senha</label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input data-testid="reset-confirm-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" className="border-slate-200 pl-9 focus-visible:ring-blue-600" />
                </div>
              </div>
              <button data-testid="reset-submit-btn" type="submit" disabled={loading} className="w-full rounded-xl bg-blue-600 py-3.5 text-sm font-bold text-white shadow-[0_8px_24px_rgba(37,99,235,0.4)] transition-colors hover:bg-blue-700 disabled:opacity-60">
                {loading ? "Salvando..." : "Redefinir senha"}
              </button>
            </form>
          )}

          <Link to="/login" data-testid="reset-back-login" className="mt-6 flex items-center justify-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700">
            <ArrowLeft className="h-4 w-4" /> Voltar para o login
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
